/* Andiani — «Mi coche»: selector de vehículo, memoria del coche y etiquetas de compatibilidad. */
(() => {
  const STORAGE_KEY = 'andiani-car';
  const BODY_LABELS = { hatch: 'Compacto', sedan: 'Sedán', suv: 'SUV', cabrio: 'Cabrio', coupe: 'Coupé', gc: 'Gran Coupé' };
  /* Códigos M Performance de BMW en un texto: «M 240i» → M240i, «M340i/d» → M340i y M340d, «M340» → M340.
     Las de dos cifras solo con i/d (M40i), para no confundirlas con roscas («M14 x 1,5») */
  const bmwCodes = (s) => [...String(s || '').matchAll(/\bM\s?(\d{3}[id]?|\d{2}[id])(?:\/([id]))?(?![\w])/g)]
    .flatMap((m) => (m[2] ? [`M${m[1]}`, `M${m[1].replace(/[id]$/, '')}${m[2]}`] : [`M${m[1]}`]));
  /* Código del título frente al del motor: con letra, exacto (M340i ≠ M340d); sin letra, por las cifras (M340 = M340i) */
  const sameCode = (t, e) => (/[id]$/.test(t) ? t === e : e.replace(/[id]$/, '') === t);
  const TOP_BRANDS = ['Audi', 'BMW', 'Cupra', 'Land Rover', 'Mercedes-Benz', 'Seat', 'Tesla', 'Volkswagen'];
  const PCX_HANDLE = 'reprogramacion-mediante-centralita-paralela';
  const PEDAL_HANDLE = 'sport-plus-pro-antirrobo';
  const FUEL = { g: 'gasolina', d: 'diésel', h: 'híbrido', e: 'eléctrico' };
  /* Versión de la centralita según la potencia de serie (las variantes del producto en la tienda) */
  const pcxRange = (e) => (!e || !e.cv || e.f === 'e' ? null : e.cv < 150 ? '< 150CV' : e.cv < 300 ? 'Entre 150CV y 300CV' : e.cv <= 400 ? 'Entre 300CV y 400CV' : null);

  /* «2.0 TFSI 310» + «310 CV» → «2.0 TFSI 310 CV» (sin repetir la potencia) */
  const engineLabel = (e) => {
    const first = (e.detail || '').split(' · ')[0];
    const num = (first.match(/^(\d+) CV$/) || [])[1];
    if (num && new RegExp(`(^|\\s)${num}$`).test(e.name)) return `${e.name} CV`;
    return e.name.includes(first) ? e.name : `${e.name} ${first}`.trim();
  };
  const norm = (text) => String(text).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
      else node.setAttribute(key, value === true ? '' : value);
    }
    for (const child of [].concat(children)) if (child) node.append(child);
    return node;
  };
  const readCar = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
    } catch {
      return null;
    }
  };
  const writeCar = (car) => {
    try {
      if (car) localStorage.setItem(STORAGE_KEY, JSON.stringify(car));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* Sin almacenamiento: el coche solo dura esta página. */
    }
  };

  class AndianiGarage extends HTMLElement {
    connectedCallback() {
      this.data = null;
      this.car = readCar();
      this.chip = this.querySelector('[data-an-chip]');
      this.chipLabel = this.querySelector('[data-an-chip-label]');
      this.chip.addEventListener('click', () => {
        this.stayOnSave = false;
        this.open();
      });
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-an-garage-open]');
        if (opener) {
          event.preventDefault();
          this.stayOnSave = opener.hasAttribute('data-an-stay');
          this.open();
        }
        const pick = event.target.closest('[data-an-vehicle]');
        if (pick) {
          event.preventDefault();
          this.pickById(pick.getAttribute('data-an-vehicle'));
        }
      });
      this.load().then(() => this.refresh());
      /* Tarjetas añadidas después de cargar la página (páginas de preparador con más de 250 productos) */
      document.addEventListener('andiani:cards-added', () => { if (this.data) this.renderBadges(this.vehicle()); });
    }

    async load() {
      if (this.data) return this.data;
      try {
        const response = await fetch(this.dataset.src);
        this.data = await response.json();
      } catch {
        this.data = { vehicles: [], universal: [], engineDependent: [] };
      }
      this.specific = new Set(this.data.vehicles.flatMap((v) => v.products));
      return this.data;
    }

    /* El coche guardado puede ser uno de Andiani (con productos o proyecto) o una generación del catálogo de DTE */
    vehicle() {
      if (!this.car || !this.data) return null;
      if (this.car.vehicleId) {
        const own = this.data.vehicles.find((v) => v.id === this.car.vehicleId);
        if (own) return own;
      }
      const c = this.car.cat;
      if (!c) return null;
      return { id: 'cat:' + c.key, brand: c.brand, model: c.model, gen: c.years || '', tag: '', products: [], catalog: true };
    }

    engine() {
      const vehicle = this.vehicle();
      if (!vehicle) return null;
      if (vehicle.engines && this.car.engineId) return vehicle.engines.find((e) => e.id === this.car.engineId) || null;
      const e = this.car.cat && this.car.cat.engine;
      if (!e) return null;
      return { id: 'cat', name: e.name, detail: [e.cv ? `${e.cv} CV` : '', FUEL[e.f] || ''].filter(Boolean).join(' · '), pcx: pcxRange(e), cv: e.cv, electric: e.f === 'e', fromCatalog: true };
    }

    /* Generación de DTE del coche guardado (para la lista de motores y el pedal) */
    catRow() {
      const c = this.car && this.car.cat;
      return c ? { bSlug: c.b, slug: c.slug, label: c.model, years: c.years, brand: c.brand } : null;
    }

    carName(withGen = true) {
      const vehicle = this.vehicle();
      if (!vehicle) return '';
      const engine = this.engine();
      return [vehicle.brand, vehicle.model, withGen ? vehicle.gen : '', engine ? engineLabel(engine) : ''].filter(Boolean).join(' ');
    }

    async pickById(id) {
      await this.load();
      const vehicle = this.data.vehicles.find((v) => v.id === id);
      if (!vehicle) return;
      await this.open();
      const row = this.cat.rows.find((r) => r.own.includes(vehicle));
      if (vehicle.engines || row) this.chooseOwn(vehicle, row || null);
      else this.save(vehicle, null);
    }

    save(vehicle, engine, row, extra) {
      /* Antes de guardar: carrocería y restyling, si ese coche los necesita */
      if (!extra) return this.askDetails(row ? `${row.bSlug}/${row.slug}` : (this.data.ownCat || {})[vehicle.id], `${vehicle.brand} ${vehicle.model}`, (x) => this.save(vehicle, engine, row, x));
      this.car = { vehicleId: vehicle.id, engineId: engine ? engine.id : null, cat: row ? this.catInfo(row, null) : null, ...extra };
      writeCar(this.car);
      this.close();
      this.refresh();
      document.dispatchEvent(new CustomEvent('andiani:car-change', { detail: { vehicle, engine } }));
      this.go(vehicle);
    }

    catInfo(row, engine) {
      return { key: `${row.bSlug}/${row.slug}`, b: row.bSlug, slug: row.slug, brand: row.brand, model: row.label, years: row.years, engine: engine ? { name: engine[0], cv: engine[1], kw: engine[2], cc: engine[3], f: engine[4] } : null };
    }

    /* Guarda una generación del catálogo (con o sin motor), enlazada o no a un vehículo de Andiani */
    saveCat(row, own, engine, extra) {
      if (!extra) return this.askDetails(`${row.bSlug}/${row.slug}`, `${row.brand} ${row.model}`, (x) => this.saveCat(row, own, engine, x));
      this.car = { vehicleId: own ? own.id : null, engineId: null, cat: this.catInfo(row, engine), ...extra };
      writeCar(this.car);
      this.close();
      this.refresh();
      document.dispatchEvent(new CustomEvent('andiani:car-change', { detail: { vehicle: this.vehicle(), engine: this.engine() } }));
      this.go(this.vehicle());
    }

    /* Quitar el coche guardado para explorar la tienda sin filtro */
    clear() {
      this.car = null;
      writeCar(null);
      this.close();
      this.refresh();
      document.dispatchEvent(new CustomEvent('andiani:car-change', { detail: { vehicle: null, engine: null } }));
    }

    refresh() {
      const vehicle = this.vehicle();
      this.chip.setAttribute('data-has-car', vehicle ? 'true' : 'false');
      this.chipLabel.textContent = vehicle ? this.carName(false) : this.chipLabel.dataset.empty;
      this.chip.setAttribute('aria-label', vehicle ? `Mi coche: ${this.carName()}. Cambiar` : 'Elegir mi coche');
      this.renderBar(vehicle);
      this.renderBadges(vehicle);
      this.renderProduct(vehicle);
      this.renderPageNotice(vehicle);
      this.renderMyCar(vehicle);
    }

    /* Página de otro coche: aviso con el camino a la del coche guardado */
    renderPageNotice(vehicle) {
      const box = document.querySelector('[data-an-page-car]');
      if (!box) return;
      const here = box.getAttribute('data-an-page-car');
      const target = this.destination(vehicle);
      if (!vehicle || !target || target === here) {
        box.hidden = true;
        return;
      }
      box.replaceChildren(
        el('span', {}, ['Esta página es para el ', el('strong', { text: box.dataset.title }), `. Tu coche es un ${this.carName()}.`]),
        el('a', { class: 'an-btn an-btn--primary', href: target, text: 'Ver lo de mi coche' })
      );
      box.hidden = false;
    }

    /* «Tu coche» de un coche sin página propia: las tarjetas de sus piezas, pedidas a la tienda por etiqueta de coche
       (/collections/all/<etiqueta> con la sección andiani-mycar-parts). Los avisos de compatibilidad los pone renderBadges. */
    async renderMyCarParts(vehicle, box) {
      const tags = vehicle.url ? [] : [...new Set((this.carTagEntries() || []).map(([t]) => t))];
      const key = tags.join('|');
      if (this._partsKey === key) return;
      this._partsKey = key;
      box.replaceChildren();
      box.hidden = true;
      if (!tags.length) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const handle = (t) => norm(t).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const seen = new Set();
      const cards = [];
      for (const tag of tags) {
        try {
          const html = await (await fetch(`${root}collections/all/${handle(tag)}?section_id=andiani-mycar-parts`)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          for (const card of doc.querySelectorAll('.an-card')) {
            const href = card.getAttribute('href');
            if (!seen.has(href)) { seen.add(href); cards.push(document.importNode(card, true)); }
          }
        } catch {}
      }
      if (this._partsKey !== key || !cards.length) return;
      box.replaceChildren(
        el('div', { class: 'an-vgroup__head' }, [
          el('h2', { class: 'an-display', style: 'font-size: clamp(30px, 3vw, 40px);', text: `Piezas para tu ${vehicle.model}` }),
          el('p', { text: 'Lo que tenemos para tu coche. Cada ficha indica las versiones, la carrocería y los motores compatibles.' }),
        ]),
        el('div', { class: 'an-grid' }, cards)
      );
      document.dispatchEvent(new CustomEvent('andiani:cards-added'));
    }

    /* «Tu coche»: todo lo que hay para el coche guardado, tenga o no página propia */
    async renderMyCar(vehicle) {
      const page = document.querySelector('[data-an-mycar]');
      if (!page) return;
      const q = (selector) => document.querySelector(selector);
      const title = q('[data-an-mycar-title]');
      const intro = q('[data-an-mycar-intro]');
      const actions = q('[data-an-mycar-actions]');
      const progBox = q('[data-an-mycar-projects]');
      const pageBox = q('[data-an-mycar-page]');
      const products = q('[data-an-mycar-products]');
      q('[data-an-mycar-body]')?.classList.toggle('is-empty', !vehicle);
      if (!vehicle) {
        title.textContent = 'Elige tu coche';
        intro.textContent = 'Dinos qué coche tienes y te enseñamos todo lo compatible: kits, potencia, sonido y programas de ABT y LUMMA.';
        actions.replaceChildren(el('button', { type: 'button', class: 'an-btn an-btn--primary', text: 'Elegir mi coche', 'data-an-garage-open': true }));
        progBox.hidden = true;
        pageBox.hidden = true;
        if (products) products.hidden = true;
        return;
      }
      await this.loadCatalog();
      const engine = this.engine();
      title.textContent = `Tu ${vehicle.brand} ${vehicle.model}`;
      intro.textContent = [vehicle.gen, engine ? [engineLabel(engine), ...engine.detail.split(' · ').slice(1)].join(' · ') : 'Motor sin indicar'].filter(Boolean).join(' · ');
      const needsEngine = !engine && (vehicle.engines || (this.car && this.car.cat));
      actions.replaceChildren(...[
        needsEngine ? this.engineSelect(vehicle, 'Indica el motor para afinar los resultados') : null,
        el('button', { type: 'button', class: 'an-btn an-btn--outline', text: 'Cambiar de coche', 'data-an-garage-open': true }),
      ].filter(Boolean));
      if (products) products.hidden = false;
      // Página propia del coche (kits con precio)
      if (vehicle.url) {
        pageBox.replaceChildren(
          el('div', {}, [el('span', { class: 'an-eyebrow', text: 'Tu coche tiene página propia' }), el('h2', { class: 'an-display an-h2', text: `Kits y tuning para tu ${vehicle.model}` }), el('p', { text: 'Carrocería, potencia y sonido con precio, compatibles con tu coche.' })]),
          el('a', { class: 'an-btn an-btn--primary', href: vehicle.url, text: 'Ver los kits de tu coche' })
        );
        pageBox.hidden = false;
      } else {
        pageBox.hidden = true;
      }
      // Coche sin página propia: sus piezas (Rieger, Je-Design…) aquí mismo
      const partsBox = q('[data-an-mycar-parts]');
      if (partsBox) this.renderMyCarParts(vehicle, partsBox);
      // Programas ABT / LUMMA
      const { list, exact } = this.projectsFor();
      if (list.length) {
        const cards = list.map((p) => el('a', { class: 'an-mycar__program', href: p.url }, [
          el('span', { class: 'an-mycar__program-media' }, p.img ? [el('img', { src: `${p.img}?width=900`, alt: '', loading: 'lazy', width: '900', height: '600' })] : []),
          el('span', { class: 'an-mycar__program-body' }, [
            el('span', { class: 'an-eyebrow', text: `${p.prep} · Programa a medida` }),
            el('strong', { class: 'an-display', text: `${p.prep} ${p.name}` }),
            el('span', { text: 'Ver el programa →' }),
          ]),
        ]));
        const note = !engine && list.some((p) => p.re)
          ? el('p', { class: 'an-mycar__note', text: 'Hay programas para varias versiones de tu coche. Indica el motor y te enseñamos el tuyo.' })
          : engine && !exact && list.length ? el('p', { class: 'an-mycar__note', text: 'Programa del modelo. Si tu versión es otra, te lo confirmamos al pedir presupuesto.' }) : null;
        progBox.replaceChildren(
          el('div', { class: 'an-section__head' }, [el('div', {}, [el('span', { class: 'an-eyebrow', text: 'Transformación completa' }), el('h2', { class: 'an-display an-h2', text: list.length > 1 ? 'Programas para tu coche' : 'El programa para tu coche' })])]),
          ...(note ? [note] : []),
          el('div', { class: 'an-mycar__programs' }, cards)
        );
        progBox.hidden = false;
      } else {
        progBox.hidden = true;
      }
    }

    /* Coches de la ficha (etiquetas como «Seat Leon 5F», de Je-Design, Rieger…) */
    productCars(box) {
      try { return JSON.parse(box.querySelector('[data-an-pdp-cars]')?.getAttribute('data-an-pdp-cars') || '[]'); } catch { return []; }
    }

    /* Generación del catálogo del coche guardado → etiquetas de coche que le corresponden ([etiqueta, nota]) */
    carTagEntries() {
      if (!this.car) return null;
      const key = (this.car.cat && this.car.cat.key) || (this.data.ownCat || {})[this.car.vehicleId];
      if (!key) return null;
      return (this.data.carTags || {})[key] || [];
    }

    /* Regla de motor de un producto (p. ej. «Solo Formentor VZ5»): con un motor de Andiani se mira su id; con uno del
       catálogo de DTE (id «cat»), la versión del rótulo en el nombre del motor («2.5 VZ5 4Drive»). Sin versión, no se descarta. */
    ruleFits(rule, engine) {
      if (rule.engines.includes(engine.id)) return true;
      if (!engine.fromCatalog) return false;
      const v = (rule.label.match(/\b(VZ5|TT RS|TTS|RS\d|S\d|M\d)\b/) || [])[1];
      return v ? new RegExp(`(^|[^A-Za-z0-9])${v.replace(' ', '\\s?')}([^A-Za-z0-9]|$)`, 'i').test(engine.name) : true;
    }

    /* Versión de una nota de etiqueta («VZ5», «RS3», «M3 berlina» → «M3»); las notas de carrocería no son versión */
    versionOf(note) {
      const m = (note || '').match(/^(VZ5|TT RS|TTS|RS\d|S\d|M\d|i4|i20 N|i30 N)\b/);
      return m ? m[1] : null;
    }

    /* Modelo deportivo según el nombre del motor («Rs6 Performance quattro» → RS6, «M5 Competition» → M5; no «M340i») */
    sportVersion() {
      const engine = this.engine();
      const m = engine && `${engine.name} ${engine.detail || ''}`.match(/(^|[^A-Za-z0-9])(RS\s?\d|M\d)([^A-Za-z0-9]|$)/i);
      return m ? m[2].replace(/\s/g, '').toUpperCase() : null;
    }

    /* Versión del coche guardado según el nombre del motor («VZ5 2.5 TSI» → VZ5), entre las de su generación */
    userVersion(entries) {
      const engine = this.engine();
      if (!engine || !entries) return null;
      const name = `${engine.name} ${engine.detail || ''}`;
      for (const [, note] of entries) {
        const v = this.versionOf(note);
        // DTE llama «2.5 RS quattro» al motor del TT RS
        const src = v === 'TT RS' ? '(TT\\s?)?RS' : v && v.replace(' ', '\\s?');
        if (v && new RegExp(`(^|[^A-Za-z0-9])${src}([^A-Za-z0-9]|$)`, 'i').test(name)) return v;
      }
      return null;
    }

    /* Compatibilidad por etiquetas de coche (fichas y tarjetas). Devuelve { tone, short, strong, span } o null.
       - ✓ si la generación coincide (y, con versión conocida por el motor, si la pieza vale para esa versión);
       - «compruébalo» si la etiqueta es una versión o carrocería concreta, o si el fabricante no la indica para tu versión;
       - «no» si es de otro coche o el título la excluye («(no VZ5)», «(no híbrido enchufable ni VZ5)»). */
    /* Perfil del motor guardado para comparar con los títulos: combustible (g/d/h/e), kW, cilindrada, 4x4 y versión.
       Motores del catálogo de DTE: datos exactos. Motores de Andiani: kW calculados desde los CV del rótulo.
       4x4 solo si el nombre lo dice (4Drive, quattro, xDrive, 4Motion); los nombres de 4x4 siempre lo llevan. */
    engineProfile() {
      const e = this.engine();
      if (!e) return null;
      const name = e.name || '';
      let fuel = null;
      let kw = [];
      const c = e.fromCatalog && this.car && this.car.cat && this.car.cat.engine;
      if (c) {
        fuel = c.f || null;
        if (c.kw) kw = [c.kw];
      } else {
        const d = (e.detail || '').toLowerCase();
        fuel = /h[ií]brido/.test(d) ? 'h' : /di[eé]sel/.test(d) ? 'd' : /el[eé]ctric/.test(d) ? 'e' : /gasolina/.test(d) ? 'g' : null;
        kw = ((e.detail || '').match(/\d{2,3}(?=\s*(?:\/|CV))/g) || []).map((cv) => Math.round(cv * 0.7355));
      }
      if (/e-?Hybrid/i.test(name)) fuel = 'h';
      return {
        fuel, kw,
        disp: (name.match(/\b(\d\.\d)\b/) || [])[1] || null,
        awd: /4Drive|quattro|xDrive|4Motion|\b4WD\b|\bAWD\b/i.test(name),
        version: /\bVZ5\b/i.test(name) ? 'VZ5' : /\bVZ\b/i.test(name) ? 'VZ' : null,
        // Código M Performance de BMW (DTE: «M240 I xDrive», «M 340i», «M340 D Mild Hybrid», «M40 I» → M240i, M340i, M340d, M40i)
        code: bmwCodes(name.replace(/\bM\s?(\d{2,3})\s?([id])\b/gi, (m, d, l) => `M${d}${l.toLowerCase()}`))[0] || null,
      };
    }

    /* Condiciones de motor del título: en positivo fuera de los paréntesis («2.0 TDI 110 kW / 2.0 TSI 140/180 kW»,
       «híbrido enchufable tracción delantera», «4Drive», «VZ5») y negadas entre paréntesis («(no híbrido enchufable ni VZ5)»).
       Los paréntesis sin negación son información («(unos 35 mm en 2.0 TSI / 15 mm en VZ5)») y no cuentan. */
    engineRule(title) {
      if (!title) return null;
      const feats = (s) => {
        const fuels = [];
        if (/h[ií]brido enchufable|e-?Hybrid|\bPHEV\b/i.test(s)) fuels.push('h');
        if (/\bTDI\b|di[eé]sel/i.test(s)) fuels.push('d');
        if (/\b(e?TSI|TFSI)\b/i.test(s.replace(/e-?Hybrid\s*\d\.\d\s*TSI/gi, ''))) fuels.push('g');
        if (/el[eé]ctrico/i.test(s)) fuels.push('e');
        const kws = [];
        for (const m of s.matchAll(/(\d{2,3}(?:\s*(?:\/|y)\s*\d{2,3})*)\s*kW/gi)) kws.push(...m[1].split(/\s*(?:\/|y)\s*/).map(Number));
        const pairs = [...s.matchAll(/(\d\.\d)\s*(e-?Hybrid|e?TSI|TFSI|TDI)/gi)].map((m) => [m[1], /hybrid/i.test(m[2]) ? 'h' : /TDI/i.test(m[2]) ? 'd' : 'g']);
        const versions = [];
        if (/\bVZ5\b/i.test(s)) versions.push('VZ5');
        if (/\bVZ\b/i.test(s)) versions.push('VZ');
        const drive = /\b4Drive\b|\b4WD\b|tracci[oó]n total|quattro|xDrive|4Motion/i.test(s) ? 'awd' : /\b2WD\b|tracci[oó]n delantera/i.test(s) ? 'fwd' : null;
        return { fuels, kws, pairs, versions, drive };
      };
      const negText = [...title.matchAll(/\(([^)]*)\)/g)].map((m) => m[1]).filter((p) => /\b(no|ni|sin|excepto)\b/i.test(p)).join(' / ');
      const neg = feats(negText);
      const pos = feats(title.replace(/\([^)]*\)/g, ' '));
      // Códigos M Performance de BMW: «solo M240i», «(M340i/d)» en positivo; «no vale para el M240i», «(no vale para el M340)» en negativo
      const negOut = [...title.matchAll(/\b(?:no vale para|no para|excepto)\s+(?:el\s+|los\s+)?M\s?\d[^,;()]*/gi)].map((m) => m[0]).join(' ');
      neg.codes = bmwCodes(`${negText} ${negOut}`);
      pos.codes = bmwCodes(title).filter((c) => !neg.codes.includes(c));
      const any = (f) => f.fuels.length || f.kws.length || f.pairs.length || f.versions.length || f.drive || f.codes.length;
      if (!any(pos) && !any(neg)) return null;
      // Las suspensiones KW tienen versión aparte para 4Drive: si el título no lo dice, son de tracción delantera
      if (/^Suspensi[oó]n roscada KW/i.test(title) && !pos.drive && (pos.kws.length || pos.pairs.length)) pos.drive = 'fwd';
      return { pos, neg };
    }

    /* 'ok' si el motor cumple las condiciones, 'no' si no, 'unknown' si falta algún dato del motor */
    engineMatch(rule, prof) {
      const { pos, neg } = rule;
      const kwIn = (list) => prof.kw.some((k) => list.some((x) => Math.abs(x - k) <= 2));
      if (prof.fuel && neg.fuels.includes(prof.fuel)) return 'no';
      if (prof.version && neg.versions.includes(prof.version)) return 'no';
      if (neg.kws.length && kwIn(neg.kws)) return 'no';
      if (prof.code && neg.codes.some((c) => sameCode(c, prof.code))) return 'no';
      // Pieza solo para un M Performance («solo M240i»): vale si el motor lo es, y no si es otro
      if (pos.codes.length) return prof.code && pos.codes.some((c) => sameCode(c, prof.code)) ? 'ok' : 'no';
      const hasPos = pos.fuels.length || pos.kws.length || pos.pairs.length || pos.versions.length || pos.drive;
      if (!hasPos) return 'ok';
      // La versión nombrada vale por sí sola («Kit de rebaje 2.0 TSI y VZ5»)
      if (prof.version && pos.versions.includes(prof.version)) return 'ok';
      const checks = [];
      if (pos.kws.length) checks.push(prof.kw.length ? kwIn(pos.kws) : null);
      else if (pos.pairs.length) checks.push(prof.disp && prof.fuel ? pos.pairs.some(([d, f]) => d === prof.disp && f === prof.fuel) : null);
      if (pos.fuels.length) checks.push(prof.fuel ? pos.fuels.includes(prof.fuel) : null);
      if (pos.drive) checks.push(pos.drive === 'awd' ? prof.awd : !prof.awd);
      if (pos.versions.length && !checks.length) checks.push(false);
      if (checks.includes(false)) return 'no';
      if (checks.includes(null)) return 'unknown';
      return 'ok';
    }

    /* Versión deportiva del motor guardado según su nombre en el catálogo («2.0 GTI», «2.0 R 4motion», «2.0 Cupra», «2.0 TSI RS») */
    perfOf(name) {
      if (/\bTCR\b/i.test(name)) return 'GTI TCR';
      if (/Clubsport/i.test(name)) return 'GTI Clubsport';
      for (const v of ['GTI', 'GTD', 'GTE', 'Cupra', 'RS', 'ST']) if (new RegExp(`\\b${v}\\b`, 'i').test(name)) return v;
      return /\bR\b(?!-)/.test(name) ? 'R' : null;
    }

    /* Versiones exactas de la pieza (metacampo andiani.versiones de Rieger: { "Volkswagen Golf 7": ["GTI", "GTD"] }, "" = básica)
       frente a la versión del motor. Los acabados (R-Line, FR, Monte Carlo) no se ven en el motor: «Si es R-Line». */
    fitByVersions(vehicle, cars, versions) {
      if (!versions || typeof versions !== 'object') return null;
      const entries = this.carTagEntries() || [];
      // La pieza lleva la etiqueta de la versión de tu motor («Audi TT RS FV» para un TT RS): eso ya lo decide la etiqueta
      const uv = this.userVersion(entries);
      if (uv && entries.some(([t, n]) => cars.includes(t) && this.versionOf(n) === uv)) return null;
      const lists = entries.filter(([t]) => cars.includes(t) && Array.isArray(versions[t])).map(([t]) => versions[t]);
      if (!lists.length) return null;
      const all = [...new Set(lists.flat())];
      const named = all.filter(Boolean);
      const name = `${vehicle.brand} ${vehicle.model}`;
      const engine = this.engine();
      // Solo versión básica: vale para todos menos los motores deportivos (Rieger nombra GTI, R, Cupra… cuando valen)
      if (all.length === 1 && all[0] === '' && !(engine && this.perfOf(engine.name))) return null;
      if (!engine) return all.includes('') ? null : { tone: 'maybe', short: 'Indica tu motor', strong: `Es para el ${named.join(', ')}`, span: 'Elige tu motor y te confirmamos si vale.' };
      const v = this.perfOf(engine.name);
      if (v) {
        if (all.includes(v)) return { tone: 'ok', short: `✓ Compatible con tu ${v}`, strong: `✓ Vale para tu ${name} ${v}`, span: 'Comprueba la carrocería y el año en «Coches compatibles».' };
        return { tone: 'no', short: 'No para tu versión', strong: `No vale para tu ${name} ${v}`, span: named.length ? `Es para el ${named.join(', ')}${all.includes('') ? ' y la versión básica' : ''}.` : 'Es para la versión básica.' };
      }
      if (all.includes('')) return null;
      const trims = named.filter((x) => ['R-Line', 'FR', 'Monte Carlo'].includes(x));
      if (trims.length) return { tone: 'maybe', keep: true, short: `Si es ${trims.join(' / ')}`, strong: `Vale si tu ${name} es ${trims.join(' o ')}`, span: 'Es para ese acabado: compruébalo en la documentación de tu coche.' };
      return { tone: 'no', short: `Solo ${named.join(' / ')}`, strong: `Es solo para el ${named.join(', ')}`, span: `Tu ${name} lleva otro motor.` };
    }

    /* Fase del coche que pide la pieza según el título: 'pre' (antes del restyling), 'post' (con restyling) o null (las dos) */
    phaseOf(title) {
      if (!title || /incluido (el )?restyling/i.test(title)) return null;
      if (/antes del facelift|\bno restyling|anterior al restyling/i.test(title)) return 'pre';
      if (/después del facelift|(?<!no |incluido |anterior al )\brestyling\b/i.test(title)) return 'post';
      return null;
    }

    /* Inicio del restyling para las etiquetas de la pieza («07/2024»), si se conoce */
    faceliftDate(cars) {
      const map = (this.data && this.data.carFacelift) || {};
      const own = (this.carTagEntries() || []).map(([t]) => t).filter((t) => cars.includes(t));
      for (const t of own.length ? own : cars) if (map[t]) return map[t];
      return '';
    }

    /* Compatibilidad completa: coche, versión, motor y fase (antes o después del restyling) */
    /* Fase que pide la pieza en tu coche: de la ficha de Rieger (metacampo andiani.fases) o, si no hay, del título */
    productPhase(cars, title, phases) {
      if (phases && typeof phases === 'object') {
        const own = (this.carTagEntries() || []).map(([t]) => t).filter((t) => cars.includes(t) && Array.isArray(phases[t]));
        if (own.length) {
          const all = own.map((t) => phases[t]);
          if (all.some((l) => !l.length)) return null;
          const set = [...new Set(all.flat())];
          return set.length === 1 ? set[0] : null;
        }
      }
      return this.phaseOf(title);
    }

    fitByTags(vehicle, cars, title = '', versions = null, bodies = null, phases = null) {
      const inner = this.fitByTagsInner(vehicle, cars, title, versions);
      if (!inner || inner.tone === 'no' || inner.version) return inner;
      const f = this.fitByBody(vehicle, cars, title, bodies, inner);
      if (f.tone === 'no' || f.body) return f;
      const ph = this.productPhase(cars, title, phases);
      if (!ph) return f;
      const name = `${vehicle.brand} ${vehicle.model}`;
      const date = this.faceliftDate(cars);
      const when = ph === 'pre' ? `anterior al restyling${date ? ` (antes de ${date})` : ''}` : `con restyling${date ? ` (desde ${date})` : ''}`;
      const user = this.car && this.car.facelift;
      if (!user) return { tone: 'maybe', keep: true, phase: true, phaseDep: true, short: ph === 'pre' ? '¿Antes del restyling?' : '¿Con restyling?', strong: `Es para el ${name} ${when}`, span: '¿Tu coche es anterior o posterior al restyling? Dínoslo y lo confirmamos.' };
      if (user !== ph) return { tone: 'no', phaseDep: true, short: ph === 'pre' ? 'Solo antes del restyling' : 'Solo con restyling', strong: `Es para el ${name} ${when}`, span: `Tu coche es ${user === 'pre' ? 'anterior al' : 'posterior al'} restyling.` };
      return { ...f, phaseDep: true };
    }

    /* Carrocerías: claves y nombres. Los datos vienen de Rieger (metacampo andiani.carrocerias), de las etiquetas de BMW
       (coupé F32, Touring F31…) y de los títulos de Je-Design («SC», «ST», «5 puertas», «(no ST)») */
    bodyKey(text) {
      const s = String(text).toLowerCase();
      if (/familiar|station wagon|\bst\b|sportstourer|sports tourer|avant|touring|combi|kombi|variant/.test(s)) return 'familiar';
      if (/fastback/.test(s)) return 'fb';
      if (/gran coup/.test(s)) return 'gc';
      if (/sed[aá]n|berlina|limousine|notchback|4 puertas/.test(s)) return 'sedan';
      if (/cabrio|convertible|roadster/.test(s)) return 'cabrio';
      if (/coup[eé]/.test(s)) return 'coupe';
      if (/\bsuv\b/.test(s)) return 'suv';
      if (/3 puertas|\bsc\b|2 puertas/.test(s)) return '3p';
      if (/5 puertas|hatchback|sportback|spaceback/.test(s)) return '5p';
      return null;
    }

    bodyLabel(key) {
      return { '3p': '3 puertas', '5p': '5 puertas', familiar: 'familiar', sedan: 'sedán', coupe: 'coupé', cabrio: 'cabrio', gc: 'Gran Coupé', fb: 'Fastback', suv: 'SUV' }[key] || key;
    }

    /* Carrocerías que existen del coche guardado (todas las de sus etiquetas) */
    genBodies() {
      const map = (this.data && this.data.carBodies) || {};
      return [...new Set((this.carTagEntries() || []).flatMap(([t]) => map[t] || []))];
    }

    /* Carrocerías para las que vale la pieza en tu coche, o null si vale para todas */
    productBodies(cars, title, bodies) {
      const entries = (this.carTagEntries() || []).filter(([t]) => cars.includes(t));
      if (!entries.length) return null;
      if (bodies && typeof bodies === 'object') {
        const lists = entries.map(([t]) => bodies[t]).filter(Array.isArray);
        if (lists.length) return lists.some((l) => !l.length) ? null : [...new Set(lists.flat())];
      }
      const notes = entries.map((e) => e[1]);
      if (notes.length && notes.every((n) => n && !this.versionOf(n))) return [...new Set(notes.map((n) => this.bodyKey(n)).filter(Boolean))];
      if (/JE DESIGN/.test(title)) {
        const find = (s) => {
          const k = [];
          if (/\bST\b|familiar|Sportstourer|Avant|Touring|Combi|Variant/i.test(s)) k.push('familiar');
          if (/\bSC\b|3 puertas/i.test(s)) k.push('3p');
          if (/5 puertas|Sportback/i.test(s)) k.push('5p');
          if (/sed[aá]n|Limousine/i.test(s)) k.push('sedan');
          if (/cabrio/i.test(s)) k.push('cabrio');
          return k;
        };
        const pos = find(title.replace(/\([^)]*\)/g, ' '));
        const neg = find([...title.matchAll(/\(([^)]*)\)/g)].map((m) => m[1]).filter((p) => /\bno\b/i.test(p)).join(' '));
        if (pos.length) return pos;
        if (neg.length) return this.genBodies().filter((b) => !neg.includes(b));
      }
      return null;
    }

    /* Carrocería: si la pieza no vale para todas las de tu coche, pregunta cuál es (o descarta si no coincide) */
    fitByBody(vehicle, cars, title, bodies, f) {
      const all = this.genBodies();
      const own = this.productBodies(cars, title, bodies);
      if (!own || !own.length || all.length < 2 || all.every((b) => own.includes(b))) return f;
      const name = `${vehicle.brand} ${vehicle.model}`;
      const labels = own.filter((b) => all.includes(b)).map((b) => this.bodyLabel(b));
      if (!labels.length) return f;
      const user = this.car && this.car.body;
      if (!user) return { tone: 'maybe', keep: true, body: true, bodyDep: true, short: `Si es ${labels.join(' / ')}`, strong: `Es para el ${name} ${labels.join(' o ')}`, span: '¿Qué carrocería tiene tu coche? Dínoslo y lo confirmamos.' };
      if (!own.includes(user)) return { tone: 'no', bodyDep: true, short: `Solo ${labels.join(' / ')}`, strong: `Es para el ${name} ${labels.join(' o ')}`, span: `Tu ${name} es ${this.bodyLabel(user)}.` };
      return { ...f, bodyDep: true };
    }

    setBody(body) {
      if (!this.car) return;
      this.car = { ...this.car, body: body || null };
      writeCar(this.car);
      this.refresh();
    }

    bodyButtons() {
      return el('div', { class: 'an-phase', role: 'group', 'aria-label': 'Carrocería' },
        this.genBodies().map((b) => el('button', { type: 'button', class: 'an-btn an-btn--outline an-phase__btn', text: this.bodyLabel(b).replace(/^./, (c) => c.toUpperCase()), onclick: () => this.setBody(b) })));
    }

    /* Fase guardada en «Mi coche» (antes o después del restyling) */
    setPhase(phase) {
      if (!this.car) return;
      this.car = { ...this.car, facelift: phase || null };
      writeCar(this.car);
      this.refresh();
    }

    phaseButtons(cars) {
      const date = this.faceliftDate(cars);
      return el('div', { class: 'an-phase', role: 'group', 'aria-label': 'Restyling' }, [
        el('button', { type: 'button', class: 'an-btn an-btn--outline an-phase__btn', text: date ? `Antes de ${date}` : 'Antes del restyling', onclick: () => this.setPhase('pre') }),
        el('button', { type: 'button', class: 'an-btn an-btn--outline an-phase__btn', text: date ? `Desde ${date}` : 'Con restyling', onclick: () => this.setPhase('post') }),
      ]);
    }

    fitByTagsInner(vehicle, cars, title = '', versions = null) {
      const f = this.fitByTagsBase(vehicle, cars, title);
      if (!f || f.tone === 'no' || f.version) return f;
      const vf = this.fitByVersions(vehicle, cars, versions);
      if (vf && vf.tone !== 'ok') return vf;
      const rule = this.engineRule(title);
      if (!rule) return vf || f;
      const name = `${vehicle.brand} ${vehicle.model}`;
      const prof = this.engineProfile();
      if (!prof) return { tone: 'maybe', short: 'Indica tu motor', strong: `Depende del motor de tu ${name}`, span: 'Elige tu motor y te confirmamos si vale.', engine: true };
      const engineName = this.engine().name;
      const m = this.engineMatch(rule, prof);
      if (m === 'no') return { tone: 'no', short: 'No para tu motor', strong: `No vale para tu motor (${engineName})`, span: 'Es para otras motorizaciones de tu coche.' };
      if (m === 'unknown') return { tone: 'maybe', short: 'Confírmalo para tu motor', strong: `Confírmalo para tu ${engineName}`, span: 'Pregúntanos antes de comprar.' };
      return { tone: 'ok', short: '✓ Compatible con tu motor', strong: `✓ Vale para tu ${name} ${engineName}`, span: 'Comprueba la carrocería y el año en «Coches compatibles».' };
    }

    fitByTagsBase(vehicle, cars, title = '') {
      if (!vehicle || !cars.length) return null;
      const list = (a) => (a.length > 3 ? `${a.slice(0, 3).join(', ')} y ${a.length - 3} más` : a.join(' y '));
      const name = `${vehicle.brand} ${vehicle.model}`;
      const entries = this.carTagEntries();
      if (entries === null) return { tone: 'maybe', short: 'Compruébalo', strong: `Es para ${list(cars)}`, span: `Si tu ${name} es uno de ellos, es compatible. Si dudas, pregúntanos.` };
      const match = entries.filter(([tag]) => cars.includes(tag));
      if (!match.length) return { tone: 'no', short: 'No para tu coche', strong: `No es para tu ${name}`, span: `Es para ${list(cars)}.`, other: true };
      // Toda la generación: etiqueta sin nota, o todas las carrocerías de la generación (BMW F32 + F33 + F36)
      const bodies = [...new Set(entries.map((e) => e[1]).filter((n) => n && !this.versionOf(n)))];
      const full = match.some((m) => !m[1]) || (bodies.length > 1 && bodies.every((n) => match.some((m) => m[1] === n)));
      const v = this.userVersion(entries);
      // Versión deportiva del motor que ninguna etiqueta de la generación nombra (RS6 en la generación A6 C7, M5 en la G30):
      // las piezas del modelo normal no se dan por buenas
      const sport = !v && this.sportVersion();
      if (sport && !entries.some((e) => (this.versionOf(e[1]) || '').toUpperCase() === sport) && !new RegExp(`\\b${sport}\\b`, 'i').test(title)) {
        return { tone: 'maybe', version: true, short: `Compruébalo para el ${sport}`, strong: `Es para el ${name}; para el ${sport} no está indicada`, span: `Pregúntanos antes de comprar si vale para tu ${sport}.` };
      }
      if (v) {
        const vr = v.replace(' ', '\\s?');
        const negated = new RegExp(`\\([^)]*\\b(no|ni|sin|excepto)\\b[^)]*\\b${vr}\\b[^)]*\\)`, 'i').test(title);
        const named = !negated && new RegExp(`\\b${vr}\\b`, 'i').test(title);
        if (negated) return { tone: 'no', short: `No para el ${v}`, strong: `No vale para tu ${name} ${v}`, span: 'El fabricante la indica para otras versiones de tu coche.' };
        if (named || match.some((m) => this.versionOf(m[1]) === v)) return { tone: 'ok', short: `✓ Compatible con tu ${v.startsWith(vehicle.model) ? v : `${vehicle.model} ${v}`}`, strong: `✓ Vale para tu ${v.startsWith(vehicle.model) ? `${vehicle.brand} ${v}` : `${name} ${v}`}`, span: 'Comprueba la carrocería y el año en «Coches compatibles».' };
        if (full) {
          // El código de tipo incluye al VZ5 («Formentor KM» es todo el Formentor): vale salvo restricción en el título.
          // Llantas y ruedas, no: el VZ5 lleva frenos más grandes y Je-Design indica aparte (categoría VZ5) las que le valen.
          if (v === 'VZ5' && /^(Llanta|Juego de (4 )?ruedas)/i.test(title)) return { tone: 'no', short: 'No indicada para el VZ5', strong: `No está indicada para el ${name} ${v}`, span: 'El VZ5 lleva frenos más grandes y Je-Design indica aparte las llantas que le valen.' };
          if (v !== 'VZ5' && entries.some((e) => this.versionOf(e[1]) === v)) return { tone: 'maybe', short: `Compruébalo para el ${v}`, strong: `Es para el ${name}; para el ${v} no está indicada`, span: `Pregúntanos antes de comprar si vale para tu ${v}.` };
          return { tone: 'ok', short: `✓ Compatible con tu ${vehicle.model}`, strong: `✓ Vale para tu ${name}`, span: 'Comprueba la versión y la carrocería en «Coches compatibles».' };
        }
        const notes = [...new Set(match.map((m) => m[1]))];
        return { tone: 'no', short: `Solo ${notes.join(' / ')}`, strong: `Es para el ${notes.join(' o el ')}, no para tu ${v}`, span: `Es para ${list(match.map((m) => m[0]))}.` };
      }
      if (full) return { tone: 'ok', short: `✓ Compatible con tu ${vehicle.model}`, strong: `✓ Vale para tu ${name}${vehicle.catalog ? '' : ' ' + vehicle.gen}`.trim(), span: 'Comprueba la versión y la carrocería en «Coches compatibles».' };
      const notes = [...new Set(match.map((m) => m[1]))];
      // Motor conocido que no es de esa versión (p. ej. Formentor 1.5 TSI en una pieza solo VZ5)
      const versions = notes.map((n) => this.versionOf(n));
      // Alguna carrocería concreta sin versión (BMW coupé F32, Touring F31, Gran Coupé G26 además del i4…): vale, y la
      // carrocería se comprueba después (fitByBody). Las versiones del motor ya se han visto arriba (userVersion)
      if (versions.some((v) => !v)) return { tone: 'ok', short: `✓ Compatible con tu ${vehicle.model}`, strong: `✓ Vale para tu ${name}`, span: 'Comprueba la carrocería y el año en «Coches compatibles».' };
      if (this.engine() && versions.every(Boolean)) return { tone: 'no', short: `Solo ${versions.join(' / ')}`, strong: `Es solo para el ${versions.join(' o el ')}`, span: `Tu ${name} lleva otro motor.`, other: true };
      return { tone: 'maybe', short: `Si es ${notes.join(' / ')}`, strong: `Vale si tu ${name} es ${notes.join(' o ')}`, span: `Es para ${list(match.map((m) => m[0]))}.` };
    }

    renderFitByTags(vehicle, cars, set, title, versions, bodies, phases) {
      if (!vehicle) {
        set('neutral', [
          el('span', { text: '¿Vale para tu coche? Guárdalo y te lo decimos.' }),
          el('button', { type: 'button', class: 'an-btn an-btn--outline', style: 'align-self:flex-start;min-height:44px', text: 'Elegir mi coche', 'data-an-garage-open': true, 'data-an-stay': true }),
        ]);
        return;
      }
      const f = this.fitByTags(vehicle, cars, title, versions, bodies, phases);
      set(f.tone, [
        el('strong', { text: f.strong }),
        el('span', { text: f.span }),
        f.body ? this.bodyButtons() : null,
        f.phase ? this.phaseButtons(cars) : null,
        f.tone === 'no' ? el('a', { href: vehicle.url || this.dataset.myCar, text: 'Ver lo que sí vale para tu coche →' }) : null,
      ]);
    }

    /* Ficha de producto: compatibilidad, motor y casillas de servicios */
    renderProduct(vehicle) {
      const box = document.querySelector('[data-an-pdp]');
      if (!box || !this.data) return;
      const handle = box.getAttribute('data-an-pdp');
      const fit = box.querySelector('[data-an-pdp-fit]');
      const engine = this.engine();
      const set = (tone, nodes) => {
        fit.className = `an-pdp__fit an-pdp__fit--${tone}`;
        fit.replaceChildren(...nodes.filter(Boolean));
        fit.hidden = false;
      };
      const specific = this.specific.has(handle);
      const engineDep = this.data.engineDependent.includes(handle);
      const universal = this.data.universal.includes(handle);
      const cars = this.productCars(box);
      if (!specific && !engineDep && !universal && cars.length) {
        const carsBox = box.querySelector('[data-an-pdp-cars]');
        let versions = null;
        try { versions = JSON.parse(carsBox?.getAttribute('data-an-pdp-versions') || 'null'); } catch {}
        let bodies = null;
        try { bodies = JSON.parse(carsBox?.getAttribute('data-an-pdp-bodies') || 'null'); } catch {}
        let phases = null;
        try { phases = JSON.parse(carsBox?.getAttribute('data-an-pdp-phases') || 'null'); } catch {}
        this.renderFitByTags(vehicle, cars, set, carsBox?.getAttribute('data-an-pdp-title') || '', versions, bodies, phases);
      } else if (!specific && !engineDep && !universal) {
        fit.hidden = true;
      } else if (!vehicle) {
        set('neutral', [
          el('span', { text: '¿Es compatible con tu coche? Guárdalo y te lo decimos.' }),
          el('button', { type: 'button', class: 'an-btn an-btn--outline', style: 'align-self:flex-start;min-height:44px', text: 'Elegir mi coche', 'data-an-garage-open': true, 'data-an-stay': true }),
        ]);
      } else if (universal) {
        set('neutral', [
          el('strong', { text: `Universal: vale para tu ${vehicle.brand} ${vehicle.model}` }),
          el('span', { text: 'Requiere instalación. Si quieres, marca abajo «Presupuesto de instalación».' }),
        ]);
      } else if (engineDep) {
        const fromDte = Boolean(this.car && this.car.cat);
        if (!engine && vehicle.engines) {
          const list = el('div', { class: 'an-pdp__engines', role: 'group', 'aria-label': 'Motores' },
            vehicle.engines.map((e) => el('button', { type: 'button', class: 'an-pdp__engine', onclick: () => this.setEngine(e) }, [el('strong', { text: e.name }), el('span', { text: e.detail })]))
          );
          set('maybe', [el('strong', { text: `¿Qué motor lleva tu ${vehicle.brand} ${vehicle.model}?` }), list]);
        } else if (!engine && fromDte) {
          set('maybe', [
            el('strong', { text: `¿Qué motor lleva tu ${vehicle.brand} ${vehicle.model}?` }),
            el('span', { text: handle === PCX_HANDLE ? 'Con el motor elegimos la versión correcta para tu potencia.' : 'Con el motor confirmamos que es compatible.' }),
            this.engineSelect(vehicle, 'Motor'),
          ]);
        } else if (!engine) {
          set('maybe', [
            el('strong', { text: `Depende del motor de tu ${vehicle.model}` }),
            el('span', { text: 'Escríbenos por WhatsApp con el motor y te confirmamos la versión antes de comprar.' }),
          ]);
        } else if (handle === PEDAL_HANDLE && fromDte) {
          /* La lista de DTE del catálogo es la del PedalBox: el pedal vale para esos motores */
          set('ok', [
            el('strong', { text: `✓ Compatible con tu ${vehicle.brand} ${vehicle.model} ${engineLabel(engine)}` }),
            el('span', { text: 'Según la lista oficial de vehículos de DTE.' }),
            el('button', { type: 'button', class: 'an-textbtn', text: 'Cambiar motor', onclick: () => this.setEngine(null) }),
          ]);
        } else if (engine.pcx && handle === PCX_HANDLE && engine.fromCatalog && !vehicle.engines) {
          /* Versión por potencia; la compatibilidad de PowerControl con cada motor la confirma DTE */
          set('maybe', [
            el('strong', {}, [`Versión para tu motor (${engine.cv} CV): `, el('span', { text: engine.pcx })]),
            el('span', { text: 'Antes de enviarlo te confirmamos con DTE que tu motor es compatible. Si no lo fuera, no lo enviamos y te devolvemos el dinero.' }),
            el('a', { class: 'an-pdp__wa', href: `https://wa.me/34600530530?text=${encodeURIComponent(`Hola, ¿me confirmáis si la centralita PowerControl X es compatible con mi ${vehicle.brand} ${vehicle.model} ${engine.name} ${engine.cv} CV antes de hacer el pedido?`)}`, target: '_blank', rel: 'noopener', text: 'Prefiero confirmarlo antes por WhatsApp →' }),
            el('button', { type: 'button', class: 'an-textbtn', text: 'Cambiar motor', onclick: () => this.setEngine(null) }),
          ]);
          this.selectVariant(engine.pcx);
        } else if (handle === PCX_HANDLE && engine.fromCatalog && (engine.electric || engine.cv > 400)) {
          set('maybe', [
            el('strong', { text: engine.electric ? 'Para coches eléctricos no hay centralita' : `Tu motor tiene ${engine.cv} CV: te hacemos presupuesto` }),
            el('span', { text: engine.electric ? 'Escríbenos y te contamos las opciones para tu coche.' : 'Las versiones de esta ficha llegan hasta 400 CV. Escríbenos por WhatsApp y te preparamos una propuesta.' }),
            el('button', { type: 'button', class: 'an-textbtn', text: 'Cambiar motor', onclick: () => this.setEngine(null) }),
          ]);
        } else if (engine.pcx && handle === PCX_HANDLE) {
          set('ok', [
            el('strong', { text: `✓ Compatible con tu ${vehicle.model} ${engineLabel(engine)}` }),
            el('span', {}, ['Versión para tu motor: ', el('strong', { text: engine.pcx })]),
            el('button', { type: 'button', class: 'an-textbtn', text: 'Cambiar motor', onclick: () => this.setEngine(null) }),
          ]);
          this.selectVariant(engine.pcx);
        } else {
          set('maybe', [
            el('strong', { text: `${vehicle.model} ${engine.name}: te lo confirmamos` }),
            el('span', { text: 'Para este motor necesitamos comprobarlo. Escríbenos por WhatsApp antes de comprar.' }),
          ]);
        }
      } else if (vehicle.products.includes(handle)) {
        const rule = vehicle.productEngines && vehicle.productEngines[handle];
        if (rule && engine && !this.ruleFits(rule, engine)) {
          set('no', [el('strong', { text: `${rule.label}: no vale para tu motor` }), el('span', { text: 'Mira en la página de tu coche las opciones compatibles.' })]);
        } else if (rule && !engine) {
          /* Producto que depende del motor: se elige aquí mismo, sin salir de la ficha */
          set('maybe', [el('strong', { text: `${rule.label}: depende del motor` }), el('span', { text: 'Elige el tuyo y te decimos si vale.' }), this.engineSelect(vehicle, 'Motor')]);
        } else {
          set('ok', [el('strong', { text: `✓ Compatible con tu ${vehicle.brand} ${vehicle.model} ${vehicle.gen}` })]);
        }
      } else {
        set('no', [
          el('strong', { text: `No es compatible con tu ${vehicle.brand} ${vehicle.model}` }),
          vehicle.url ? el('a', { href: vehicle.url, text: 'Ver lo que sí vale para tu coche →' }) : el('span', { text: 'Escríbenos y te ayudamos a encontrar lo tuyo.' }),
        ]);
      }
      /* Con el motor conocido, la versión (potencia) se elige sola y se ocultan sus botones */
      const locked = Boolean(vehicle && engine && engine.pcx && handle === PCX_HANDLE && engineDep);
      document.documentElement.classList.toggle('an-variant-locked', locked);
      this.bindServices(box);
      this.fillCarField(vehicle);
    }

    /* Productos DTE: el campo «Tu coche» se rellena con «Mi coche» salvo que el cliente lo haya escrito */
    /* Campo «Tu coche» de las fichas DTE:
       - Con coche elegido en «Mi coche», el campo muestra ese coche y no se edita (se cambia con el selector).
       - Sin motor conocido, se pide el motor aparte (de la lista o escrito), y el valor enviado siempre es coche + motor.
       - Sin coche elegido, es texto libre. */
    fillCarField(vehicle) {
      const input = document.querySelector('[data-an-car-input]');
      if (!input) return;
      const field = input.closest('[data-an-car-field]');
      if (!input.dataset.bound) {
        input.dataset.bound = 'true';
        input.addEventListener('input', () => {
          if (!input.readOnly) input.dataset.manual = input.value;
          clearCarError(input);
        });
        this.carTools = el('div', { class: 'an-pdp__car-tools' });
        this.carEngine = el('input', { type: 'text', id: `${input.id}-motor`, maxlength: '80', autocomplete: 'off', placeholder: 'Ej.: 2.0 TDI 150 CV, o «no lo sé»', 'data-an-car-engine': true });
        this.carEngineBox = el('div', { class: 'an-pdp__car-engine', hidden: true }, [el('label', { for: this.carEngine.id, text: 'Motor' }), this.carEngine]);
        this.carEngine.addEventListener('input', () => {
          this.composeCarValue();
          clearCarError(input);
        });
        this.carHidden = el('input', { type: 'hidden', 'data-an-car-value': true, disabled: true });
        input.after(this.carHidden, this.carEngineBox, this.carTools);
      }
      this.carBase = '';
      const engine = this.engine();
      const label = field.querySelector(`label[for="${input.id}"]`);
      if (label && !label.dataset.text) label.dataset.text = label.textContent;
      if (label) label.textContent = vehicle ? 'Tu coche (de «Mi coche»)' : label.dataset.text;
      const NAME = 'properties[Vehículo]';
      if (!vehicle) {
        input.name = NAME;
        this.carHidden.removeAttribute('name');
        this.carHidden.disabled = true;
        input.readOnly = false;
        input.value = input.dataset.manual || '';
        delete input.dataset.needsEngine;
        this.carEngineBox.hidden = true;
        this.carTools.replaceChildren();
        field.classList.remove('is-locked');
      } else {
        input.removeAttribute('name');
        this.carHidden.name = NAME;
        this.carHidden.disabled = false;
        input.readOnly = true;
        field.classList.add('is-locked');
        this.carBase = `${vehicle.brand} ${vehicle.model} ${vehicle.gen}`.replace(/\s+/g, ' ').trim();
        const change = el('button', { type: 'button', class: 'an-textbtn', text: engine ? 'Cambiar coche o motor' : 'Cambiar coche', onclick: () => { this.stayOnSave = true; this.open(); } });
        if (engine) {
          input.value = `${this.carBase} · ${engine.name} ${engine.detail}`;
          this.carHidden.value = input.value;
          delete input.dataset.needsEngine;
          this.carEngineBox.hidden = true;
          this.carTools.replaceChildren(change);
        } else {
          input.dataset.needsEngine = 'true';
          input.value = this.carBase;
          this.carEngineBox.hidden = false;
          const canPick = vehicle.engines || (this.car && this.car.cat);
          const pick = canPick ? el('button', { type: 'button', class: 'an-textbtn', text: 'Elegir el motor de la lista', onclick: () => { this.stayOnSave = true; this.open().then(() => (vehicle.engines ? this.showEngines(vehicle, this.catRow()) : this.showCatEngines(this.catRow(), vehicle.catalog ? null : vehicle))); } }) : null;
          this.carTools.replaceChildren(...[pick, change].filter(Boolean));
          this.composeCarValue();
        }
      }
      clearCarError(input);
    }

    composeCarValue() {
      const input = document.querySelector('[data-an-car-input]');
      if (!input || !this.carBase || !input.dataset.needsEngine) return;
      this.carHidden.value = `${this.carBase} · motor: ${this.carEngine.value.trim()}`;
    }

    setEngine(engine) {
      this.car = { ...this.car, engineId: engine && !engine.fromCatalog ? engine.id : null };
      if (!engine && this.car.cat) this.car.cat = { ...this.car.cat, engine: null };
      writeCar(this.car);
      this.refresh();
    }

    selectVariant(value) {
      const radio = [...document.querySelectorAll('variant-picker input[type="radio"], .variant-picker input[type="radio"]')].find((input) => input.value === value);
      if (radio) {
        if (!radio.checked) radio.click();
        return;
      }
      for (const select of document.querySelectorAll('variant-picker select, .variant-picker select')) {
        const option = [...select.options].find((o) => o.value === value || o.text.trim() === value);
        if (option && select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    bindServices(box) {
      const services = box.querySelector('[data-an-services]');
      if (!services || services.dataset.bound) return;
      services.dataset.bound = 'true';
      const cp = services.querySelector('[data-an-cp]');
      const cpInput = cp && cp.querySelector('input');
      const sync = () => {
        const any = [...services.querySelectorAll('[data-an-service]')].some((c) => c.checked);
        if (cp) cp.hidden = !any;
        if (cpInput) cpInput.disabled = !any;
      };
      services.addEventListener('change', sync);
      sync();
    }

    renderBar(vehicle) {
      let bar = document.querySelector('[data-an-garage-bar]');
      if (!bar) {
        bar = el('div', { class: 'an-garage-bar', 'data-an-garage-bar': true, role: 'status' });
        const anchor = document.getElementById('header-group') || document.querySelector('header');
        if (anchor) anchor.after(bar);
        else document.body.prepend(bar);
      }
      bar.hidden = !vehicle;
      bar.replaceChildren();
      if (!vehicle) return;
      const engine = this.engine();
      const link = this.destination(vehicle);
      const onVehiclePage = location.pathname.replace(/\/$/, '') === link;
      const addEngine = !engine && (vehicle.engines || (this.car && this.car.cat));
      const inner = el('div', { class: 'an-wrap an-garage-bar__inner' }, [
        el('span', {}, ['Comprando para ', el('strong', { text: `${vehicle.brand} ${vehicle.model} ${vehicle.gen}${engine ? ', ' + engineLabel(engine) : ''}`.replace(/\s+,/, ',').trim() })]),
        vehicle.tag ? el('span', { text: vehicle.tag, style: 'color: var(--an-text-2)' }) : null,
        onVehiclePage ? null : el('a', { href: link, text: 'Ver todo para tu coche →' }),
        addEngine ? el('button', { type: 'button', text: 'Añadir motor', onclick: () => { this.stayOnSave = true; this.open().then(() => (vehicle.engines ? this.showEngines(vehicle, this.catRow()) : this.showCatEngines(this.catRow(), vehicle.catalog ? null : vehicle))); } }) : null,
        el('button', { type: 'button', text: 'Cambiar', onclick: () => this.open() }),
        el('button', { type: 'button', class: 'an-garage-bar__clear', 'aria-label': 'Quitar mi coche y ver toda la tienda', title: 'Quitar mi coche', onclick: () => this.clear() }, [el('span', { 'aria-hidden': 'true', text: '×' }), el('span', { text: 'Quitar' })]),
      ]);
      bar.append(inner);
    }

    renderBadges(vehicle) {
      const engine = this.engine();
      /* En la página de tu coche solo se ve lo compatible: nunca lo que no vale y, con el motor elegido,
         tampoco lo que no se puede confirmar para ese motor (salvo centralita y pedal, que confirma DTE) */
      const pageBox = document.querySelector('[data-an-page-car]');
      const ownPage = Boolean(vehicle && pageBox && this.destination(vehicle) === pageBox.getAttribute('data-an-page-car'));
      let hidden = 0;
      let needBody = false;
      let needPhase = false;
      for (const badge of document.querySelectorAll('[data-an-fit]')) {
        const handle = badge.getAttribute('data-an-fit');
        let text = '';
        let tone = '';
        let strictMaybe = false;
        if (vehicle && this.data) {
          if (this.data.universal.includes(handle)) {
            text = 'Universal · requiere instalación';
            tone = 'maybe';
          } else if (this.data.engineDependent.includes(handle)) {
            const fromDte = Boolean(this.car && this.car.cat);
            if (handle === PEDAL_HANDLE && fromDte && engine) {
              text = '✓ Compatible';
              tone = 'ok';
            } else if (handle === PCX_HANDLE && engine && engine.pcx && engine.fromCatalog && !vehicle.engines) {
              text = `Versión ${engine.pcx}`;
              tone = 'maybe';
            } else if (handle === PCX_HANDLE && engine && engine.pcx) {
              text = `✓ Compatible · ${engine.pcx}`;
              tone = 'ok';
            } else if ((vehicle.engines || fromDte) && !engine) {
              text = 'Indica tu motor';
              tone = 'maybe';
            } else {
              text = 'Te lo confirmamos para tu motor';
              tone = 'maybe';
            }
          } else if (vehicle.products.includes(handle)) {
            const rule = vehicle.productEngines && vehicle.productEngines[handle];
            if (rule && engine && !this.ruleFits(rule, engine)) {
              text = `${rule.label}: no para tu motor`;
              tone = 'no';
            } else if (rule && !engine) {
              text = `${rule.label}: indica tu motor`;
              tone = 'maybe';
            } else {
              text = `✓ Compatible con tu ${vehicle.model}`;
              tone = 'ok';
            }
          } else if (badge.getAttribute('data-an-fit-cars') && badge.getAttribute('data-an-fit-cars') !== '[]') {
            /* Piezas con etiquetas de coche (Je-Design, Rieger…) */
            let cars = [];
            try { cars = JSON.parse(badge.getAttribute('data-an-fit-cars')); } catch {}
            let versions = null;
            try { versions = JSON.parse(badge.getAttribute('data-an-fit-versions') || 'null'); } catch {}
            let bodies = null;
            try { bodies = JSON.parse(badge.getAttribute('data-an-fit-bodies') || 'null'); } catch {}
            let phases = null;
            try { phases = JSON.parse(badge.getAttribute('data-an-fit-phases') || 'null'); } catch {}
            const f = this.fitByTags(vehicle, cars, badge.getAttribute('data-an-fit-title') || '', versions, bodies, phases);
            if (f && f.phaseDep) needPhase = true;
            if (f) { text = f.short; tone = f.tone; strictMaybe = !f.keep; if (f.bodyDep) needBody = true; }
          } else if (this.specific.has(handle)) {
            text = 'No para tu coche';
            tone = 'no';
          }
        }
        badge.textContent = text;
        badge.className = `an-fit an-fit--${tone || 'no'}${badge.classList.contains('an-fit--card') ? ' an-fit--card' : ''}`;
        badge.hidden = !text;
        const card = badge.closest('.an-complete__card, [data-an-item], .an-card');
        if (card) {
          const hide = (ownPage || Boolean(card.closest('[data-an-mycar-parts]'))) && (tone === 'no' || (engine && tone === 'maybe' && strictMaybe));
          card.hidden = hide;
          if (hide) hidden++;
        }
      }
      if (pageBox) this.renderCarFilter(vehicle, ownPage, hidden, needBody, needPhase);
      /* «Tu coche»: el apartado de piezas solo se ve si queda alguna compatible */
      const parts = document.querySelector('[data-an-mycar-parts]');
      if (parts && parts.childElementCount) parts.hidden = ![...parts.querySelectorAll('.an-card')].some((c) => !c.hidden);
      /* Avisa a las listas que ordenan por compatibilidad (páginas de preparador) */
      document.dispatchEvent(new CustomEvent('andiani:badges'));
    }

    /* Página de coche filtrada: oculta apartados vacíos, actualiza los contadores de la barra y explica el filtro */
    renderCarFilter(vehicle, ownPage, hidden, needBody, needPhase) {
      for (const sec of document.querySelectorAll('.an-vgroup[id], #completa')) {
        const cards = [...sec.querySelectorAll('.an-card, .an-complete__card')];
        const visible = cards.filter((c) => !c.hidden).length;
        sec.hidden = cards.length > 0 && visible === 0;
        const link = document.querySelector(`.an-subnav a[href="#${sec.id}"]`);
        if (link) {
          link.hidden = sec.hidden;
          const count = link.querySelector('span');
          if (count && cards.length) count.textContent = `(${visible})`;
        }
      }
      let note = document.querySelector('[data-an-car-filter]');
      if (!ownPage) { if (note) note.hidden = true; return; }
      if (!note) {
        const nav = document.querySelector('.an-subnav');
        if (!nav) return;
        note = el('div', { class: 'an-wrap an-car-filter', 'data-an-car-filter': true, role: 'status' });
        nav.after(note);
      }
      const engine = this.engine();
      note.replaceChildren(
        el('span', {}, engine
          ? ['Solo ves lo compatible con tu ', el('strong', { text: `${vehicle.brand} ${vehicle.model} ${engine.name}` }), '.']
          : ['Ves lo compatible con tu ', el('strong', { text: `${vehicle.brand} ${vehicle.model}` }), '. Elige tu motor para confirmar las piezas que dependen de él.']),
        el('button', { type: 'button', class: 'an-textbtn', text: engine ? 'Cambiar motor' : 'Elegir motor', 'data-an-garage-open': true, 'data-an-stay': true }),
      );
      /* Si hay piezas que dependen del restyling, se pregunta (o se muestra lo elegido, con opción de cambiarlo) */
      /* Carrocería: se pregunta si alguna pieza de la página depende de ella */
      if (needBody && this.genBodies().length > 1) {
        const body = this.car && this.car.body;
        note.append(el('div', { class: 'an-car-filter__phase' }, body
          ? [el('span', { text: `Carrocería: ${this.bodyLabel(body)}.` }), el('button', { type: 'button', class: 'an-textbtn', text: 'Cambiar', onclick: () => this.setBody(null) })]
          : [el('span', { text: `¿Qué carrocería tiene tu ${vehicle.model}? Algunas piezas cambian.` }), this.bodyButtons()]));
      }
      if (needPhase) {
        const cars = (this.carTagEntries() || []).map(([t]) => t);
        const date = this.faceliftDate(cars);
        const phase = this.car && this.car.facelift;
        note.append(el('div', { class: 'an-car-filter__phase' }, phase
          ? [el('span', { text: `Restyling: ${phase === 'pre' ? 'anterior' : 'posterior'}${date ? ` (${phase === 'pre' ? 'antes de' : 'desde'} ${date})` : ''}.` }),
            el('button', { type: 'button', class: 'an-textbtn', text: 'Cambiar', onclick: () => this.setPhase(null) })]
          : [el('span', { text: `¿Tu ${vehicle.model} es anterior o posterior al restyling${date ? ` de ${date}` : ''}? Algunas piezas cambian.` }), this.phaseButtons(cars)]));
      }
      note.hidden = false;
    }

    /* Panel */

    async open() {
      await this.load();
      if (!this.panel) this.buildPanel();
      this.lastFocus = document.activeElement;
      this.panel.setAttribute('data-open', 'true');
      document.documentElement.style.overflow = 'hidden';
      await this.loadCatalog();
      this.showSearch('');
      return this;
    }

    close() {
      if (!this.panel) return;
      this.panel.setAttribute('data-open', 'false');
      document.documentElement.style.overflow = '';
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    /* Catálogo de DTE (marcas, generaciones y motores). Solo se descarga al abrir el selector. */
    async loadCatalog() {
      if (this.cat) return this.cat;
      try {
        const index = await (await fetch(this.dataset.catalog)).json();
        const linked = new Set();
        /* Programas ABT de cada generación; los de una versión concreta (TTS, RS6, Golf R…) llevan el patrón de su motor */
        const programs = (index.p || []).map(([prep, name, url, img]) => ({ prep, name, url, img }));
        const rows = index.g.map((r, i) => {
          const ids = r[5] || [];
          ids.forEach((id) => linked.add(id));
          const projects = (r[8] || []).map(([pi, re]) => ({ ...programs[pi], re: re ? new RegExp(re, 'i') : null })).filter((p) => p.url);
          return { i, brand: index.b[r[0]], bSlug: index.s[r[0]], model: r[1], label: r[2], years: r[3], slug: r[4], own: ids.map((id) => this.data.vehicles.find((v) => v.id === id)).filter(Boolean), partial: Boolean(r[6]), engines: r[7], projects };
        });
        this.cat = { rows, linked, version: index.fuente || '1', engines: {} };
      } catch {
        this.cat = { rows: [], linked: new Set(), version: '0', engines: {} };
      }
      return this.cat;
    }

    /* Generación del catálogo del coche guardado (o la enlazada a un vehículo de Andiani) */
    savedRow() {
      if (!this.cat || !this.car) return null;
      const c = this.car.cat;
      if (c) return this.cat.rows.find((r) => r.bSlug === c.b && r.slug === c.slug) || null;
      const vehicle = this.vehicle();
      return vehicle ? this.cat.rows.find((r) => r.own.includes(vehicle)) || null : null;
    }

    /* Programas ABT/LUMMA para el coche guardado. Con motor: el de su versión (TTS, RS6…) o, si no hay, el del modelo.
       Sin motor: todos los de la generación, primero el del modelo. */
    projectsFor() {
      const vehicle = this.vehicle();
      if (!vehicle) return { list: [], exact: false };
      const engine = this.engine();
      const row = this.savedRow();
      let list = row ? row.projects : [];
      let exact = false;
      if (engine && list.length) {
        const match = list.filter((p) => p.re && p.re.test(engine.name));
        exact = match.length > 0;
        list = exact ? match : list.filter((p) => !p.re);
      } else {
        list = [...list].sort((a, b) => (a.re ? 1 : 0) - (b.re ? 1 : 0));
      }
      if (!list.length && vehicle.project) {
        list = [{ prep: /lumma/i.test(vehicle.project) ? 'LUMMA' : 'ABT', name: `${vehicle.brand} ${vehicle.model}`, url: vehicle.project, img: '' }];
      }
      return { list, exact };
    }

    /* Página a la que se va al elegir coche: la del coche si la tiene; si no, «Tu coche» con todo lo compatible */
    destination(vehicle = this.vehicle()) {
      if (!vehicle) return null;
      return vehicle.url || this.dataset.myCar || '/pages/mi-coche';
    }

    /* Al guardar un coche: desde una ficha de producto no se sale (se quiere ver la compatibilidad de ese producto) */
    go(vehicle) {
      const target = this.destination(vehicle);
      const stay = this.stayOnSave;
      this.stayOnSave = false;
      const onProduct = location.pathname.includes('/products/');
      if (target && !stay && !onProduct && location.pathname.replace(/\/$/, '') !== target) location.assign(target);
    }

    /* Motor elegido de la lista de DTE sin salir de la página (desde la ficha o «Tu coche») */
    setCatEngine(engine) {
      if (!this.car || !this.car.cat) return;
      this.car = { ...this.car, engineId: null, cat: { ...this.car.cat, engine: engine ? { name: engine[0], cv: engine[1], kw: engine[2], cc: engine[3], f: engine[4] } : null } };
      writeCar(this.car);
      this.refresh();
      document.dispatchEvent(new CustomEvent('andiani:car-change', { detail: { vehicle: this.vehicle(), engine: this.engine() } }));
    }

    /* Selector de motor en la propia página (lista de Andiani o de DTE) */
    engineSelect(vehicle, label = 'Elige tu motor') {
      const id = `an-engine-${Math.random().toString(36).slice(2, 8)}`;
      const select = el('select', { id, class: 'an-engine-select__input' }, [el('option', { value: '', text: 'Cargando motores…' })]);
      const box = el('div', { class: 'an-engine-select' }, [el('label', { for: id, text: label }), select]);
      const fill = (items) => {
        select.replaceChildren(el('option', { value: '', text: items.length ? 'Selecciona…' : 'No tenemos la lista: escríbenos' }), ...items.map(([value, text]) => el('option', { value, text })));
      };
      if (vehicle.engines) {
        fill(vehicle.engines.map((e) => [e.id, `${e.name} · ${e.detail}`]));
        select.addEventListener('change', () => { const e = vehicle.engines.find((x) => x.id === select.value); if (e) this.setEngine(e); });
      } else if (this.car && this.car.cat) {
        this.loadCatalog().then(async () => {
          const row = this.catRow();
          const engines = row ? await this.catEngines(row) : [];
          fill(engines.map((e, i) => [String(i), [e[0], e[1] ? `${e[1]} CV` : '', FUEL[e[4]] || ''].filter(Boolean).join(' · ')]));
          select.addEventListener('change', () => { const e = engines[+select.value]; if (select.value !== '' && e) this.setCatEngine(e); });
        });
      } else {
        return null;
      }
      return box;
    }

    async catEngines(row) {
      if (!this.cat.engines[row.bSlug]) {
        const url = `${this.dataset.catalogBase}andiani-cat-${row.bSlug}.json?v=${encodeURIComponent(this.cat.version)}`;
        try {
          this.cat.engines[row.bSlug] = await (await fetch(url)).json();
        } catch {
          this.cat.engines[row.bSlug] = {};
        }
      }
      return this.cat.engines[row.bSlug][row.slug] || [];
    }

    buildPanel() {
      this.titleEl = el('h2', { class: 'an-garage-panel__title', id: 'an-garage-title' });
      this.stepsNav = el('div', { class: 'an-steps', 'aria-label': 'Pasos' });
      this.body = el('div', { style: 'display:flex;flex-direction:column;gap:14px' });
      const dialog = el('div', { class: 'an-garage-panel__dialog', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'an-garage-title' }, [
        el('div', { class: 'an-garage-panel__head' }, [this.titleEl, el('button', { type: 'button', class: 'an-garage-panel__close', 'aria-label': 'Cerrar', text: '×', onclick: () => this.close() })]),
        this.stepsNav,
        this.body,
        el('p', { class: 'an-garage-panel__note', text: 'Lo recordamos mientras navegas y te marcamos qué es compatible.' }),
      ]);
      this.panel = el('div', { class: 'an-garage-panel', 'data-open': 'false' }, [el('div', { class: 'an-garage-panel__backdrop', onclick: () => this.close() }), dialog]);
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.body.append(this.panel);
    }

    setStep(step, title) {
      this.titleEl.textContent = title;
      const labels = ['Marca y modelo', 'Generación', 'Motor (opcional)', 'Carrocería y año'];
      this.stepsNav.replaceChildren(
        ...labels.flatMap((label, index) => [
          index ? el('span', { 'aria-hidden': 'true', text: '›' }) : null,
          el('span', { text: `${index + 1} ${label}`, 'aria-current': index + 1 === step ? 'step' : null }),
        ]).filter(Boolean)
      );
    }

    /* Modelos para la búsqueda: generaciones del catálogo agrupadas por marca y modelo,
       más los vehículos de Andiani que no están enlazados a una generación (proyectos ABT o LUMMA) */
    groups() {
      if (this._groups) return this._groups;
      const map = new Map();
      const add = (brand, model, item) => {
        const key = norm(`${brand}|${model}`);
        if (!map.has(key)) map.set(key, { brand, model, items: [] });
        map.get(key).items.push(item);
      };
      for (const row of this.cat.rows) add(row.brand, row.model, { row });
      for (const v of this.data.vehicles) if (!this.cat.linked.has(v.id)) add(v.brand, v.model, { vehicle: v });
      for (const g of map.values()) {
        const own = g.items.flatMap((it) => (it.vehicle ? [it.vehicle] : it.row.own));
        const tags = [...new Set(own.map((v) => v.tag))];
        /* Los programas ABT de sus generaciones también sirven para buscar («tts», «s3», «golf r»…) */
        const programs = g.items.flatMap((it) => (it.row ? it.row.projects : []));
        g.tag = tags.find((t) => t && t !== 'Consúltanos') || (programs.length ? 'Programa ABT' : '');
        const names = programs.map((p) => p.name.replace(/^(\S+\s)/, '')).join(' ');
        g.hay = norm(`${g.brand} ${g.model} ${names} ${names.replace(/\s+/g, '')} ${g.items.map((it) => (it.row ? `${it.row.label} ${it.row.years}` : `${it.vehicle.gen} ${it.vehicle.years || ''}`)).join(' ')}`);
        /* BMW: el catálogo dice «4»; en España se busca «Serie 4», y los M por su nombre (M3, M4…) */
        if (/^bmw$/i.test(g.brand) && /^\d\b/.test(g.model)) {
          const n = g.model.match(/^\d/)[0];
          const labels = g.items.map((it) => (it.row ? it.row.label : '')).join(' ');
          g.hay += ` serie ${n} serie${n}`;
          for (const [re, m] of [[/F87|G87/, 'm2'], [/F80|G80|G81/, 'm3'], [/F82|F83|G82|G83/, 'm4'], [/F90/, 'm5']]) if (re.test(labels)) g.hay += ` ${m}`;
        }
      }
      this._groups = [...map.values()].sort((a, b) => (b.tag ? 1 : 0) - (a.tag ? 1 : 0) || (a.brand + a.model).localeCompare(b.brand + b.model, 'es'));
      return this._groups;
    }

    showSearch(query) {
      this.setStep(1, '¿Qué coche tienes?');
      const input = el('input', { type: 'search', value: query, placeholder: 'golf 8, formentor, clase g…', autocomplete: 'off', 'aria-controls': 'an-results' });
      const results = el('div', { id: 'an-results', class: 'an-list', role: 'list' });
      const brandsWrap = el('div', {});
      const MAX = 40;
      const update = () => {
        const words = norm(input.value.trim()).split(/\s+/).filter(Boolean);
        this.lastWords = words;
        brandsWrap.hidden = words.length > 0;
        results.hidden = words.length === 0;
        if (!words.length) return;
        const found = this.groups().filter((g) => words.every((w) => g.hay.includes(w)));
        results.replaceChildren(
          ...found.slice(0, MAX).map((g) => el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => (g.items.length === 1 ? this.pickItem(g.items[0]) : this.showGens(g)) }, [
            el('span', {}, [el('strong', { text: g.brand }), ` ${g.model}`]),
            g.tag ? el('span', { class: 'an-row__tag an-row__tag--ok', text: g.tag }) : null,
          ]))
        );
        if (found.length > MAX) results.append(el('p', { style: 'margin:0;padding:12px 14px;font-size:14px;color:var(--an-text-2)', text: `Y ${found.length - MAX} modelos más: escribe también el modelo para afinar.` }));
        if (!found.length) {
          results.append(el('p', { style: 'margin:0;padding:14px;font-size:15px;color:var(--an-text-2)', text: 'No encontramos ese coche. Prueba con la marca y el modelo (por ejemplo, «seat ibiza») o escríbenos por WhatsApp y te ayudamos.' }));
        }
      };
      input.addEventListener('input', update);
      const brands = [...new Set([...this.cat.rows.map((r) => r.brand), ...this.data.vehicles.map((v) => v.brand)])].sort((a, b) => a.localeCompare(b, 'es'));
      const top = TOP_BRANDS.filter((b) => brands.includes(b));
      const brandRow = el('div', { class: 'an-brands' });
      const fillBrands = (list, withAll) => {
        brandRow.replaceChildren(
          ...list.map((brand) => el('button', { type: 'button', class: 'an-brand', text: brand, onclick: () => { input.value = brand; update(); input.focus(); } })),
          ...(withAll ? [el('button', { type: 'button', class: 'an-brand', style: 'border-color: var(--an-text)', text: `Todas (${brands.length})`, onclick: () => fillBrands(brands, false) })] : [])
        );
      };
      fillBrands(top, brands.length > top.length);
      brandsWrap.append(el('span', { class: 'an-finder__label', text: 'Marcas', style: 'margin-bottom:8px' }), brandRow);
      const current = this.vehicle()
        ? el('div', { class: 'an-garage-current' }, [
            el('span', {}, ['Ahora: ', el('strong', { text: this.carName() })]),
            el('button', { type: 'button', class: 'an-textbtn', text: 'Quitar mi coche', onclick: () => this.clear() }),
          ])
        : null;
      /* Siempre visible: si el coche no aparece, que nos escriba */
      const help = el('div', { class: 'an-garage-help' }, [
        el('strong', { text: '¿No encuentras tu coche?' }),
        el('span', { text: 'Escríbenos con la marca, el modelo, el año y el motor, y te decimos qué hay para él.' }),
        el('span', { class: 'an-garage-help__links' }, [
          el('a', { href: `https://wa.me/34600530530?text=${encodeURIComponent('Hola, no encuentro mi coche en la web. Es un ')}`, target: '_blank', rel: 'noopener', text: 'WhatsApp 600 53 05 30' }),
          el('a', { href: '/pages/contact', text: 'Formulario de contacto' }),
        ]),
      ]);
      this.body.replaceChildren(...[current, el('label', {}, ['Escribe tu coche', input]), results, brandsWrap, help].filter(Boolean));
      update();
      requestAnimationFrame(() => input.focus());
    }

    showGens(group) {
      this.setStep(2, `${group.brand} ${group.model}`);
      const items = [...group.items].sort((a, b) => {
        const ya = parseInt(a.row ? a.row.years : a.vehicle.years || '0', 10) || 0;
        const yb = parseInt(b.row ? b.row.years : b.vehicle.years || '0', 10) || 0;
        return yb - ya;
      });
      const list = el('div', { class: 'an-list', role: 'list' },
        items.map((it) => {
          if (it.row) {
            const tag = it.row.own.map((v) => v.tag).find((t) => t && t !== 'Consúltanos') || (it.row.projects.length ? 'Programa ABT' : '');
            return el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => this.pickItem(it) }, [
              el('span', {}, [el('strong', { text: it.row.label }), el('span', { class: 'an-row__sub', text: [it.row.years, `${it.row.engines} motores`].filter(Boolean).join(' · ') })]),
              tag ? el('span', { class: 'an-row__tag an-row__tag--ok', text: tag }) : null,
            ]);
          }
          const v = it.vehicle;
          return el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => this.pickItem(it) }, [
            el('span', {}, [el('strong', { text: `${v.model} ${v.gen}` }), el('span', { class: 'an-row__sub', text: [BODY_LABELS[v.body], v.years].filter(Boolean).join(' · ') })]),
            v.tag && v.tag !== 'Consúltanos' ? el('span', { class: 'an-row__tag an-row__tag--ok', text: v.tag }) : null,
          ]);
        })
      );
      this.body.replaceChildren(list, el('button', { type: 'button', class: 'an-textbtn', text: '‹ Buscar otro coche', onclick: () => this.showSearch('') }));
      list.querySelector('button')?.focus();
    }

    /* Elegir una generación del catálogo o un vehículo de Andiani */
    pickItem(item) {
      if (item.vehicle) return this.selectVehicle(item.vehicle);
      const row = item.row;
      if (row.own.length === 1 && !row.partial) return this.chooseOwn(row.own[0], row);
      if (row.own.length) return this.showVersions(row);
      return this.showCatEngines(row, null);
    }

    /* La generación incluye varios vehículos de Andiani (carrocerías o versiones con productos distintos) */
    showVersions(row) {
      this.setStep(2, `${row.brand} ${row.label}: ¿qué versión?`);
      const list = el('div', { class: 'an-list', role: 'list' }, [
        ...row.own.map((v) => el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => this.chooseOwn(v, row) }, [
          el('span', {}, [el('strong', { text: `${v.model} ${v.gen}` }), el('span', { class: 'an-row__sub', text: [BODY_LABELS[v.body], v.years].filter(Boolean).join(' · ') })]),
          v.tag && v.tag !== 'Consúltanos' ? el('span', { class: 'an-row__tag an-row__tag--ok', text: v.tag }) : null,
        ])),
        row.partial ? el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => this.showCatEngines(row, null) }, [el('span', {}, [el('strong', { text: 'Otra versión' }), el('span', { class: 'an-row__sub', text: `${row.label} ${row.years}` })])]) : null,
      ].filter(Boolean));
      this.body.replaceChildren(list, el('button', { type: 'button', class: 'an-textbtn', text: '‹ Buscar otro coche', onclick: () => this.showSearch('') }));
      list.querySelector('button')?.focus();
    }

    chooseOwn(vehicle, row) {
      if (vehicle.engines) this.showEngines(vehicle, row);
      else this.showCatEngines(row, vehicle);
    }

    selectVehicle(vehicle) {
      if (vehicle.engines) this.showEngines(vehicle);
      else this.save(vehicle, null);
    }

    showEngines(vehicle, row = null) {
      this.setStep(3, `${vehicle.model}: ¿qué motor?`);
      const list = el('div', { class: 'an-list', role: 'list' },
        vehicle.engines.map((e) => el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => this.save(vehicle, e, row) }, [el('strong', { text: e.name }), el('span', { class: 'an-row__tag', text: e.detail })]))
      );
      this.body.replaceChildren(
        el('p', { style: 'margin:0;font-size:15px;color:var(--an-text-2);line-height:1.5', text: 'Solo hace falta para potencia y centralitas. Si no lo sabes, sáltalo y te lo preguntaremos cuando importe.' }),
        list,
        el('button', { type: 'button', class: 'an-btn an-btn--outline', style: 'align-self:flex-start;min-height:44px', text: 'No lo sé, saltar', onclick: () => this.save(vehicle, null, row) })
      );
      list.querySelector('button')?.focus();
    }

    /* Paso 4: carrocería (si el coche tiene varias con piezas distintas) y restyling (si las piezas cambian con él).
       Las dos con «No lo sé»: la página del coche lo preguntará solo cuando importe. */
    askDetails(key, name, done) {
      const tags = ((this.data.carTags || {})[key] || []).map(([t]) => t);
      const bodies = [...new Set(tags.flatMap((t) => (this.data.carBodies || {})[t] || []))];
      const date = tags.map((t) => (this.data.carFacelift || {})[t]).find(Boolean);
      const extra = { body: null, facelift: null };
      const note = (text) => el('p', { style: 'margin:0;font-size:15px;color:var(--an-text-2);line-height:1.5', text });
      const choice = (label, sub, fn) => el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: fn }, [el('span', {}, [el('strong', { text: label }), sub ? el('span', { class: 'an-row__sub', text: sub }) : null])]);
      const skip = (fn) => el('button', { type: 'button', class: 'an-btn an-btn--outline', style: 'align-self:flex-start;min-height:44px', text: 'No lo sé, saltar', onclick: fn });
      const askPhase = () => {
        if (!date) return done(extra);
        this.setStep(4, `${name}: ¿de qué año es?`);
        const list = el('div', { class: 'an-list', role: 'list' }, [
          choice(`Anterior a ${date}`, 'Antes del restyling', () => { extra.facelift = 'pre'; done(extra); }),
          choice(`De ${date} en adelante`, 'Con el restyling', () => { extra.facelift = 'post'; done(extra); }),
        ]);
        this.body.replaceChildren(note(`El modelo se renovó en ${date} (restyling) y algunas piezas cambian. Lo ves en la ficha técnica o el permiso de circulación.`), list, skip(() => done(extra)));
        list.querySelector('button')?.focus();
      };
      if (bodies.length < 2) return askPhase();
      this.setStep(4, `${name}: ¿qué carrocería?`);
      const list = el('div', { class: 'an-list', role: 'list' }, bodies.map((b) => choice(this.bodyLabel(b).replace(/^./, (c) => c.toUpperCase()), '', () => { extra.body = b; askPhase(); })));
      this.body.replaceChildren(note('Algunas piezas solo valen para una carrocería.'), list, skip(askPhase));
      list.querySelector('button')?.focus();
    }

    /* Motores de la generación según la lista de DTE */
    async showCatEngines(row, own) {
      if (!row) return this.showSearch('');
      await this.loadCatalog();
      this.setStep(3, `${row.brand} ${row.label}: ¿qué motor?`);
      this.body.replaceChildren(el('p', { style: 'margin:0;color:var(--an-text-2)', text: 'Cargando motores…' }));
      const all = await this.catEngines(row);
      /* Primero los motores que coinciden con lo buscado (p. ej. «tts»); se marca la versión con programa ABT */
      const words = (this.lastWords || []).filter((w) => w.length > 1 && !norm(`${row.brand} ${row.model}`).includes(w));
      const hit = (e) => words.some((w) => norm(e[0]).replace(/\s+/g, '').includes(w));
      const engines = words.length ? [...all.filter(hit), ...all.filter((e) => !hit(e))] : all;
      const programFor = (e) => row.projects.find((p) => p.re && p.re.test(e[0]));
      const list = el('div', { class: 'an-list', role: 'list' },
        engines.map((e) => {
          const program = programFor(e);
          return el('button', { type: 'button', class: 'an-row', role: 'listitem', onclick: () => this.saveCat(row, own, e) }, [
            el('span', {}, [el('strong', { text: e[0] }), el('span', { class: 'an-row__sub', text: [e[1] ? `${e[1]} CV` : '', FUEL[e[4]] || ''].filter(Boolean).join(' · ') })]),
            program ? el('span', { class: 'an-row__tag an-row__tag--ok', text: `${program.prep} ${program.name.replace(/^\S+\s/, '')}` }) : null,
          ]);
        })
      );
      this.body.replaceChildren(
        el('p', { style: 'margin:0;font-size:15px;color:var(--an-text-2);line-height:1.5', text: row.projects.some((p) => p.re) ? 'El motor nos dice tu versión (por ejemplo, TTS o RS) para enseñarte su programa, la potencia y las centralitas. Si no lo sabes, sáltalo.' : 'Solo hace falta para potencia y centralitas. Si no lo sabes, sáltalo y te lo preguntaremos cuando importe.' }),
        engines.length ? list : el('p', { style: 'margin:0', text: 'No tenemos la lista de motores de esta generación.' }),
        el('button', { type: 'button', class: 'an-btn an-btn--outline', style: 'align-self:flex-start;min-height:44px', text: 'No lo sé, saltar', onclick: () => this.saveCat(row, own, null) }),
        el('button', { type: 'button', class: 'an-textbtn', text: '‹ Buscar otro coche', onclick: () => this.showSearch('') })
      );
      list.querySelector('button')?.focus();
    }
  }

  if (!customElements.get('an-garage')) customElements.define('an-garage', AndianiGarage);

  /* Disponibilidad en el fabricante de la variante elegida: el tema no vuelve a pintar el aviso al cambiar de
     acabado o de lado, así que se actualiza aquí con el mapa de la ficha (data-an-stock-map) */
  const STOCK_TEXT = {
    stock: ['En stock en el fabricante.', ' Te lo enviamos en cuanto nos llega.'],
    pocas: ['Quedan pocas unidades en el fabricante.', ''],
    pedido: ['Se fabrica bajo pedido.', ' Te confirmamos el plazo de entrega antes de enviarlo.'],
    sin: ['Sin stock en el fabricante ahora mismo.', ' Te confirmamos el plazo de entrega antes de enviarlo.'],
  };
  const updateStock = () => {
    const mapEl = document.querySelector('[data-an-stock-map]');
    const box = document.querySelector('[data-an-stock]');
    if (!mapEl || !box) return;
    let map = {};
    try { map = JSON.parse(mapEl.textContent); } catch { return; }
    const id = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    const st = map[id];
    if (!st || !STOCK_TEXT[st]) { box.hidden = true; return; }
    box.className = `an-pdp__stock an-pdp__stock--${st}`;
    box.replaceChildren(el('strong', { text: STOCK_TEXT[st][0] }), STOCK_TEXT[st][1]);
    box.hidden = false;
  };
  document.addEventListener('variant:update', () => setTimeout(updateStock, 50));
  document.addEventListener('change', (event) => { if (event.target.closest && event.target.closest('variant-picker')) setTimeout(updateStock, 500); });

  /* Campo «Tu coche» obligatorio: el formulario de Horizon no valida (novalidate), así que se comprueba
     antes de que lo envíe. Sin coche o sin motor no se añade al carrito. */
  function clearCarError(input) {
    const field = input.closest('[data-an-car-field]');
    if (!field) return;
    field.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
    field.querySelector('[data-an-car-engine]')?.removeAttribute('aria-invalid');
    field.querySelector('[data-an-car-error]')?.remove();
  }
  /* En la ventana y en captura: así va antes que los eventos delegados de Horizon (en document) */
  window.addEventListener('submit', (event) => {
    const input = event.target.querySelector?.('[data-an-car-input]');
    if (!input) return;
    const hidden = input.readOnly ? input.closest('[data-an-car-field]')?.querySelector('[data-an-car-value]') : null;
    const value = (hidden ? hidden.value : input.value).trim();
    const missingEngine = /motor:\s*$/i.test(value);
    if (value.length >= 4 && !missingEngine) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const field = input.closest('[data-an-car-field]');
    clearCarError(input);
    field.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
    const message = el('p', {
      class: 'an-pdp__car-error',
      id: `${input.id}-error`,
      role: 'alert',
      'data-an-car-error': true,
      text: missingEngine ? 'Añade el motor de tu coche (o escribe «no lo sé»).' : 'Escribe tu coche para poder preparar tu pedido correctamente.',
    });
    input.setAttribute('aria-describedby', message.id);
    input.after(message);
    const engineInput = field.querySelector('[data-an-car-engine]');
    if (missingEngine && engineInput && !engineInput.closest('[hidden]')) {
      engineInput.setAttribute('aria-invalid', 'true');
      engineInput.setAttribute('aria-describedby', message.id);
      engineInput.focus();
    } else {
      input.focus();
      if (missingEngine && !input.readOnly) input.setSelectionRange(input.value.length, input.value.length);
    }
  }, true);

  /* Formulario de contacto: si llega ?proyecto=…, se escribe la consulta en el mensaje */
  document.addEventListener('DOMContentLoaded', () => {
    const proyecto = new URLSearchParams(location.search).get('proyecto');
    const body = document.querySelector('textarea[name="contact[body]"]');
    if (!proyecto || !body || body.value.trim()) return;
    body.value = `Hola, me interesa el proyecto ${proyecto.slice(0, 120)}. Mi coche es: `;
  });
})();
